/**
 * locales.ts — canonical locale metadata for the AstroGenesis web client.
 *
 * Programme rules (i18n 2026-08-27):
 *  - Locales are addressed by PATH PREFIX (/hi/today), never by query
 *    params or cookies alone — path prefixes are what search engines
 *    can index and hreflang can point at.
 *  - The chart wheel's geometry is never mirrored in RTL (zodiacal order
 *    is astronomical fact). Only chrome mirrors. This file only carries
 *    the `dir` metadata; components must not read it to flip charts.
 *
 * Adding a locale = add one entry here (+ drop a ui.json + one resources
 * line in ./index.ts).
 */

export type LocaleDir = "ltr" | "rtl";

export interface LocaleMeta {
  /** URL + i18n code. Used as the /:locale path prefix. */
  code: string;
  /** English display name (for docs, admin UIs). */
  name: string;
  /** Endonym — what the switcher shows. */
  nativeName: string;
  /** Text direction for chrome. NEVER applied to chart geometry. */
  dir: LocaleDir;
}

export const LOCALES: LocaleMeta[] = [
  { code: "en",      name: "English",              nativeName: "English",        dir: "ltr" },
  { code: "de",      name: "German",               nativeName: "Deutsch",        dir: "ltr" },
  { code: "fr",      name: "French",               nativeName: "Français",       dir: "ltr" },
  { code: "id",      name: "Indonesian",           nativeName: "Bahasa Indonesia", dir: "ltr" },
  { code: "ar",      name: "Arabic",               nativeName: "العربية",         dir: "rtl" },
  { code: "ur",      name: "Urdu",                 nativeName: "اردو",            dir: "rtl" },
  { code: "hi",      name: "Hindi",                nativeName: "हिन्दी",           dir: "ltr" },
  { code: "bn",      name: "Bengali",              nativeName: "বাংলা",           dir: "ltr" },
  { code: "ta",      name: "Tamil",                nativeName: "தமிழ்",           dir: "ltr" },
  { code: "te",      name: "Telugu",               nativeName: "తెలుగు",          dir: "ltr" },
  { code: "zh-Hans", name: "Chinese (Simplified)", nativeName: "简体中文",        dir: "ltr" },
  { code: "ja",      name: "Japanese",             nativeName: "日本語",          dir: "ltr" },
];

/** QA-only pseudo locale. It is intentionally omitted from the switcher and
 * production sitemap, but remains addressable by CI and visual tests. */
export const QA_LOCALE_CODES = ["ar-XB"] as const;

export const DEFAULT_LOCALE = "en";

/** Language of the hi pilot. Full list ships in config/SEO from day one. */
export const PILOT_LOCALES = ["hi"];

export const RTL_LOCALES = LOCALES.filter((l) => l.dir === "rtl").map((l) => l.code);

export const RTL_LOCALE_CODES = new Set([...RTL_LOCALES, "ar-XB"]);

/** Codes handed to i18next `supportedLngs` (order matters for the switcher). */
export const SUPPORTED_LOCALE_CODES = [...LOCALES.map((l) => l.code), ...QA_LOCALE_CODES];

export const LOCALE_CODES = new Set(SUPPORTED_LOCALE_CODES);

/** localStorage key for the user's explicit choice (detect chain step b). */
export const LOCALE_STORAGE_KEY = "ag.locale";

export function isSupportedLocale(code: unknown): code is string {
  return typeof code === "string" && LOCALE_CODES.has(code);
}

export function localeDir(code: string): LocaleDir {
  return RTL_LOCALE_CODES.has(code) ? "rtl" : "ltr";
}

export function localeMeta(code: string): LocaleMeta | undefined {
  return LOCALES.find((l) => l.code === code);
}

/**
 * splitLocalePrefix — parse "/hi/today/abc" into { locale: "hi", path: "/today/abc" }
 * and "/today/abc" into { locale: null, path: "/today/abc" }.
 * Only a FIRST segment that is a supported locale counts; every legacy
 * single-segment route (/cast, /faq, …) is unaffected.
 */
export function splitLocalePrefix(
  pathname: string,
): { locale: string | null; path: string } {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0 && isSupportedLocale(segments[0])) {
    return { locale: segments[0], path: "/" + segments.slice(1).join("/") };
  }
  return { locale: null, path: pathname };
}
